import { ErrorHandler, Inject, Injectable } from '@angular/core';
import { SessionIdService } from './session-id.service';
import { TELEMETRY_CONFIG, TelemetryConfig } from './telemetry.config';

const DEFAULT_MAX_ERRORS_PER_MINUTE = 10;

/**
 * ErrorHandler global: ademas de loguear a consola (comportamiento default),
 * reporta el error al endpoint /telemetry/errors del BFF, que lo emite como
 * LogRecord OTel hacia Loki. El browser nunca habla con el collector.
 *
 * Usa fetch (no HttpClient) a proposito: evita pasar por los interceptors
 * HTTP — un error dentro de un interceptor no puede realimentarse a si mismo.
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  readonly #config: TelemetryConfig;
  readonly #sessionId: SessionIdService;
  #windowStart = 0;
  #reportedInWindow = 0;

  constructor(
    @Inject(TELEMETRY_CONFIG) config: TelemetryConfig,
    sessionId: SessionIdService
  ) {
    this.#config = config;
    this.#sessionId = sessionId;
  }

  handleError(error: unknown): void {
    // eslint-disable-next-line no-console
    console.error(error);
    if (!this._shouldReport()) {
      return;
    }
    const normalized =
      error instanceof Error ? error : new Error(String(error));
    try {
      void fetch(this.#config.errorsEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
        body: JSON.stringify({
          message: normalized.message.slice(0, 2000),
          stack: normalized.stack?.slice(0, 20000),
          url: location.href.slice(0, 2000),
          sessionId: this.#sessionId.getSessionId(),
          app: this.#config.appName,
        }),
      });
    } catch {
      // El reporte de errores nunca debe generar un error nuevo.
    }
  }

  private _shouldReport(): boolean {
    const now = Date.now();
    if (now - this.#windowStart > 60_000) {
      this.#windowStart = now;
      this.#reportedInWindow = 0;
    }
    const max =
      this.#config.maxErrorsPerMinute ?? DEFAULT_MAX_ERRORS_PER_MINUTE;
    if (this.#reportedInWindow >= max) {
      return false;
    }
    this.#reportedInWindow += 1;
    return true;
  }
}
